import type React from 'react';

import { useInView } from 'react-intersection-observer';

import { Spinner } from '@change/ds-core-ui';

import { usePetitionDetailsPageData } from '#src/app/pages/petitionDetails/pageContext';
import { LazyComponent } from '#src/app/shared/components/layout/lazy';

import { ScrollTrackingWaypointContainer } from '../../../../../ScrollTrackingWaypoint';
import { useSignaturesDistributionContext } from '../../shared/provider';
import { SignatureDistributionSummary } from '../SignatureDistributionSummary';
import type { SignatureMapProps } from '../SignatureMap';

type Props = {
	isStaff: boolean;
};

/**
 * Lazily loads the `SignatureMap` component in its own chunk. SignatureMap pulls in maplibre (via
 * the design system's Map/MapMarker), so loading it this way keeps maplibre out of the petition
 * page's initial bundle and fetches it only when the map scrolls into view. The dynamic import
 * must target the whole SignatureMap component (the sole maplibre importer) — see the note in
 * SignatureMap about the upcoming @change/ds-map extraction.
 */
const loadSignatureMap = async (): Promise<React.ComponentType<SignatureMapProps>> =>
	(await import(/* webpackChunkName: "pages/petitionDetails-signatureMap" */ '../SignatureMap')).SignatureMap;

const LazySignatureMap: React.FC<SignatureMapProps> = (props) => {
	const { ref, inView } = useInView({
		triggerOnce: true,
		rootMargin: '0px 200px',
	});

	const placeholder = (
		<div className="mb-4 flex h-[400px] w-full items-center justify-center rounded-lg">
			<Spinner />
		</div>
	);

	if (!inView) {
		return <div ref={ref}>{placeholder}</div>;
	}

	return (
		<LazyComponent
			loadComponent={loadSignatureMap}
			fallback={placeholder}
			/* oxlint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/consistent-type-assertions */
			{...(props as any)}
		/>
	);
};

export const SignaturesDistributionInfo: React.FC<Props> = ({ isStaff }) => {
	const {
		data: {
			petition: { id },
		},
	} = usePetitionDetailsPageData();
	const {
		data: { isLoadingData, isCrunchingData, signaturesClusters, clusterPercentage },
	} = useSignaturesDistributionContext();

	return (
		<>
			{!isLoadingData && !isCrunchingData && signaturesClusters && clusterPercentage !== undefined && (
				<div className="mt-4 mb-2 flex flex-col">
					<ScrollTrackingWaypointContainer
						name="petition_sign_map_view"
						trackingData={{ petition_id: id, is_staff: isStaff }}
					/>
					<LazySignatureMap
						signaturesClusters={signaturesClusters}
						clusterPercentage={clusterPercentage}
						isStaff={isStaff}
					/>
					<SignatureDistributionSummary signaturesClusters={signaturesClusters} clusterPercentage={clusterPercentage} />
				</div>
			)}
		</>
	);
};
